import { Injectable } from '@angular/core';
import { Observable, share } from 'rxjs';
import { ApiResponse } from 'src/app/shared/models/api-response.interface';
import { HttpService } from './http.service';
import { UsersGroup } from '../../../shared/enums/users-group.enum';
import { ThirdPartyFullObject } from 'src/app/shared/models/third-party.interface';
import { NavigationTabSettings } from '../../../shared/models/navigation-tab-settings.interface';
import { API_VERSIONS } from '../../../shared/consts/api-versions.const';
import { DashboardType } from 'src/app/shared/enums/dashboard-types.enum';
import { UpdateUserPasswordBody } from 'src/app/shared/models/users/update-user-password.model';

@Injectable({
  providedIn: 'root',
})
export class UserApiService {
  private entityUrl = '/users';

  constructor(private apiService: HttpService) {}

  getById(id: number | string): Observable<ApiResponse> {
    return this.apiService.getReq(`${this.entityUrl}/${id}`);
  }

  getAll(
    group?: UsersGroup,
    params = {}
  ): Observable<ApiResponse<ThirdPartyFullObject[]>> {
    const updatedParams = group
      ? { filter_by_group: group, ...params }
      : { ...params };
    return this.apiService.getReq(`/${API_VERSIONS.USERS}${this.entityUrl}`, {
      params: updatedParams,
    });
  }

  getUsersPaged(
    group: UsersGroup,
    params: any = {}
  ): Observable<ApiResponse> {
    const updatedParams = {
      filter_by_group: group,
      ...params,
    };

    return this.apiService.getReq(
      `/${API_VERSIONS.USERS}${this.entityUrl}/paging`,
      {
        params: updatedParams,
      }
    );
  }

  getBrokersPaginated(page: number, search: string = ''): Observable<ApiResponse> {
    return this.apiService.getReq(
      `/${API_VERSIONS.USERS}${this.entityUrl}/paging`,
      {
        params: { filter_by_group: UsersGroup.Brokers, page, search },
      }
    );
  }

  getDealersPaginated(
    page: number,
    search: string = '',
    filter_by_supervisor: string = ''
  ): Observable<ApiResponse> {
    const params: any = {
      filter_by_group: UsersGroup.Dealers,
      page,
      search
    };
    
    if (filter_by_supervisor) {
      params.filter_by_supervisor = filter_by_supervisor;
    }
    
    return this.apiService.getReq(
      `/${API_VERSIONS.USERS}${this.entityUrl}/paging`,
      { params }
    );
  }

  getAgentsPaginated(
    page: number,
    search: string = '',
    filter_by_supervisor: string = ''
  ): Observable<ApiResponse> {
    const params: any = {
      filter_by_group: UsersGroup.Agents,
      page,
      search
    };
    
    if (filter_by_supervisor) {
      params.filter_by_supervisor = filter_by_supervisor;
    }
    
    return this.apiService.getReq(
      `/${API_VERSIONS.USERS}${this.entityUrl}/paging`,
      { params }
    );
  }

  toggleAccessToAllClaims(userId: number): Observable<ApiResponse> {
    return this.apiService.putReq(
      `${this.entityUrl}/${userId}/toggle-access-all-claims`
    );
  }

  toggleAccessToFortegraClaims(userId: number): Observable<ApiResponse> {
    return this.apiService.putReq(
      `${this.entityUrl}/${userId}/toggle-access-fortegra-claims`
    );
  }

  toggleIsLoginSessionExpirable(userId: number): Observable<ApiResponse> {
    return this.apiService.putReq(
      `${this.entityUrl}/${userId}/toggle-is-login-session-expirable`
    );
  }

  toggleIsFortegraUser(userId: number): Observable<ApiResponse> {
    return this.apiService.putReq(
      `${this.entityUrl}/${userId}/toggle-is-fortegra-user`
    );
  }

  toggleIsExternalPartner(userId: number, creationSource?: string): Observable<ApiResponse> {
    return this.apiService.putReq(
      `${this.entityUrl}/${userId}/toggle-is-external-partner`,
      creationSource ? { creation_source: creationSource } : {}
    );
  }

  getGhostAccessToken(userId: number): Observable<ApiResponse> {
    return this.apiService.getReq(`/auth/${userId}/ghost-access-token`);
  }

  getAllNavigationTabs(
    params: any = {}
  ): Observable<ApiResponse<NavigationTabSettings[]>> {
    return this.apiService.getReq(`/navigation-tabs`, { params });
  }

  getNavigationTabsById(
    id: number | string,
    dashboard?: DashboardType
  ): Observable<ApiResponse<NavigationTabSettings[]>> {
    if (dashboard) {
      return this.apiService.getReq(
        `/navigation-tabs?filter_by_group=${dashboard}`
      );
    }
    return this.apiService.getReq(`${this.entityUrl}/${id}/navigation-tabs`);
  }

  setNavigationTabsById(
    id: number | string,
    navigationTabIds: number[] = []
  ): Observable<ApiResponse> {
    return this.apiService.putReq(`${this.entityUrl}/${id}/navigation-tabs`, {
      navigation_tab_ids: navigationTabIds,
    });
  }

  toggleNavigationTabsAccess(
    id: number | string,
    haveAccessToAllTabs = true
  ): Observable<ApiResponse> {
    return this.apiService.putReq(
      `${this.entityUrl}/${id}/toggle-navigation-tabs-access`,
      haveAccessToAllTabs
    );
  }

  deleteDealer(id: number): Observable<ApiResponse> {
    return this.apiService.deleteReq(`/dealers/${id}`);
  }

  addSharedDocuments(
    userId: number,
    shared_documents: File[]
  ): Observable<ApiResponse> {
    let formData = new FormData();

    shared_documents.forEach((file, index) => {
      formData.append(`shared_documents[]`, file, file.name);
    });

    return this.apiService.postReq(
      `${this.entityUrl}/${userId}/add-shared-documents`,
      formData
    );
  }

  getSharedDocuments(
    userId: number,
    params: { page: number; length: number }
  ): Observable<ApiResponse> {
    return this.apiService.getReq(
      `${this.entityUrl}/${userId}/get-shared-documents`,
      { params }
    );
  }

  deleteUser(id: number): Observable<ApiResponse> {
    return this.apiService.deleteReq(`${this.entityUrl}/${id}`);
  }

  /**
   * Fetches user activity logs for a given user ID.
   */
  getUserActivity(
    userId: number | string,
    params = {}
  ): Observable<ApiResponse> {
    return this.apiService.getReq(`/users/activity/${userId}`, { params });
  }

  assignDealerGroupToUser(
    dealer_group_id: number,
    dealer_id: number
  ): Observable<ApiResponse> {
    return this.apiService.postReq(`/users/assign-dealer-group`, {
      dealer_group_id,
      dealer_id,
    });
  }

  removeUserFromDealerGroup(user_id: number): Observable<ApiResponse> {
    return this.apiService.getReq(`/users/remove-dealer-from-group/${user_id}`);
  }

  updateUserPassword(
    id: number,
    body: UpdateUserPasswordBody
  ): Observable<ApiResponse> {
    return this.apiService.putReq(`/users/${id}/update-password`, body);
  }

  toggleUserStatus(id: number): Observable<ApiResponse> {
    return this.apiService.putReq(`/users/${id}/toggle-status`);
  }
}
